import { QueryClient, QueryFunction } from "@tanstack/react-query";

const TOKEN_KEY = "rently_token";

function getAuthHeaders(): Record<string, string> {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function notifyUnauthorized() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
  window.dispatchEvent(new Event("rently:unauthorized"));
}

async function throwIfResNotOk(res: Response) {
  if (res.status === 401) {
    notifyUnauthorized();
  }
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  arg1: string,
  arg2?: string | unknown,
  arg3?: unknown,
): Promise<Response> {
  let url: string;
  let method: string;
  let data: unknown;

  const isMethod = (s: any) => typeof s === "string" && /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/i.test(s);

  if (isMethod(arg1) && typeof arg2 === "string") {
    method = arg1.toUpperCase();
    url = arg2;
    data = arg3;
  } else {
    url = arg1;
    method = typeof arg2 === "string" ? arg2.toUpperCase() : "GET";
    data = arg3 !== undefined ? arg3 : (typeof arg2 !== "string" ? arg2 : undefined);
  }

  const res = await fetch(url, {
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      ...getAuthHeaders(),
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      headers: getAuthHeaders(),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      notifyUnauthorized();
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 30000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
